import React, { useState } from 'react';
import { message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../hooks/useProjectStore';
import { fractureAPI } from '../../services/api';

// /fracture/library — 方案模板库 (常见井型预置, 一键应用为新方案)
const TH = {
  bg: '#FFFFFF', text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', headerBorder: '#D6DAE0', card: '#F9FAFB',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

type Tmpl = {
  key: string; name: string; scene: string; desc: string;
  lateral: [number, number];
  design: {
    stage_count: number; cluster_per_stage: number; cluster_spacing: number;
    stage_spacing: number; pump_rate: number; fluid_type: string;
    proppant_type: string; max_proppant_conc: number;
    fluid_per_stage: number; prop_per_stage: number;
  };
};

const TEMPLATES: Tmpl[] = [
  {
    key: 'shale_gas', name: '页岩气水平井', scene: '四川盆地 / 龙马溪组',
    desc: '密切割 + 大排量大液量, 小段距高簇数, 滑溜水为主造复杂缝网',
    lateral: [3200, 4000],
    design: { stage_count: 20, cluster_per_stage: 8, cluster_spacing: 8, stage_spacing: 40, pump_rate: 16, fluid_type: '滑溜水', proppant_type: '40/70目石英砂', max_proppant_conc: 220, fluid_per_stage: 1800, prop_per_stage: 110 },
  },
  {
    key: 'tight_oil', name: '致密油水平井', scene: '鄂尔多斯 / 长7',
    desc: '中等段距 + 线性胶携砂, 兼顾缝长与导流, 中砂比',
    lateral: [2600, 3400],
    design: { stage_count: 14, cluster_per_stage: 5, cluster_spacing: 12, stage_spacing: 60, pump_rate: 12, fluid_type: '滑溜水+线性胶', proppant_type: '30/50目陶粒', max_proppant_conc: 280, fluid_per_stage: 1500, prop_per_stage: 95 },
  },
  {
    key: 'cbm', name: '煤层气直井/水平井', scene: '沁水盆地 / 煤层',
    desc: '低排量小规模 + 活性水, 防煤粉伤害, 低砂比短段',
    lateral: [900, 1400],
    design: { stage_count: 6, cluster_per_stage: 3, cluster_spacing: 15, stage_spacing: 80, pump_rate: 8, fluid_type: '活性水', proppant_type: '20/40目石英砂', max_proppant_conc: 140, fluid_per_stage: 800, prop_per_stage: 40 },
  },
];

export default function FractureLibraryPage() {
  const { currentProject } = useProjectStore();
  const nav = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);

  const apply = (t: Tmpl) => {
    if (!currentProject) return;
    Modal.confirm({
      title: `应用模板「${t.name}」?`,
      content: `将基于该模板创建一个新方案 (${t.design.stage_count} 段),不影响现有方案。`,
      onOk: async () => {
        setBusy(t.key);
        try {
          const [a, b] = t.lateral;
          const seg = (b - a) / t.design.stage_count;
          const stages = Array.from({ length: t.design.stage_count }, (_, i) => ({
            stage_number: i + 1,
            top_perf: Math.round(a + i * seg),
            bottom_perf: Math.round(a + (i + 1) * seg),
            fluid_volume: t.design.fluid_per_stage,
            proppant_mass: t.design.prop_per_stage,
          }));
          await fractureAPI.importPlan({
            project_id: currentProject.id,
            design: {
              name: `${t.name} (模板)`,
              stage_count: t.design.stage_count,
              cluster_per_stage: t.design.cluster_per_stage,
              cluster_spacing: t.design.cluster_spacing,
              stage_spacing: t.design.stage_spacing,
              pump_rate: t.design.pump_rate,
              fluid_type: t.design.fluid_type,
              proppant_type: t.design.proppant_type,
              max_proppant_conc: t.design.max_proppant_conc,
              total_fluid: t.design.fluid_per_stage * t.design.stage_count,
              total_proppant: t.design.prop_per_stage * t.design.stage_count,
              is_active: false,
            },
            stages,
          });
          message.success(`已创建「${t.name} (模板)」方案`);
          nav('/fracture/designs');
        } catch (e: any) {
          const d = e?.response?.data;
          message.error(typeof d === 'string' ? d : d?.detail || '应用失败');
        } finally { setBusy(null); }
      },
    });
  };

  if (!currentProject) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>设计体系库</h2>
        <span style={{ fontSize: 12, color: TH.textMuted }}>选一个井型模板, 一键生成新方案再到「方案管理」微调</span>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {TEMPLATES.map((t) => (
            <div key={t.key} style={{ background: TH.card, border: `1px solid ${TH.headerBorder}`, borderRadius: 6, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: TH.text }}>{t.name}</div>
                <div style={{ fontSize: 11, color: TH.textMuted }}>{t.scene}</div>
              </div>
              <div style={{ fontSize: 12, color: TH.textSec, lineHeight: 1.5 }}>{t.desc}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, fontSize: 11 }}>
                {[
                  ['段数', t.design.stage_count], ['簇/段', t.design.cluster_per_stage], ['段距m', t.design.stage_spacing],
                  ['排量', t.design.pump_rate], ['最高砂比', t.design.max_proppant_conc], ['液体', ''],
                ].map(([l, v], i) => i < 5 ? (
                  <div key={i}><div style={{ color: TH.textMuted, fontSize: 9 }}>{l}</div><div style={{ fontFamily: TH.fontMono, fontWeight: 600 }}>{v}</div></div>
                ) : null)}
              </div>
              <div style={{ fontSize: 11, color: TH.textSec }}>{t.design.fluid_type} · {t.design.proppant_type}</div>
              <button onClick={() => apply(t)} disabled={busy === t.key} style={{
                marginTop: 4, padding: '6px 0', borderRadius: 4, fontSize: 12, fontWeight: 600,
                background: busy === t.key ? '#C5C9D2' : TH.accent, color: '#fff', border: 'none', cursor: busy === t.key ? 'wait' : 'pointer',
              }}>{busy === t.key ? '创建中…' : '应用为新方案'}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
